import { useMemo, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Skeleton,
  Button,
  ConfirmDialog,
  FormField,
  PeopleField,
} from "@playbookai/ui";
import {
  createTenantPeopleSource,
  type createIntegrationAdminApi,
} from "@playbookai/api-clients";
import { searchTenantPeople } from "../../../api/tenant-people-source.js";
import { resolveDirectoryNames } from "../../../api/people-directory-api.js";

type Api = ReturnType<typeof createIntegrationAdminApi>;

/**
 * PB-4542 — who may use this integration.
 *
 * DEFAULT DENY, and the empty state says so explicitly rather than looking like
 * a loading glitch: an integration nobody is granted is switched on and unusable,
 * which is a state an administrator needs to recognise on sight.
 *
 * People are chosen from the tenant directory, by name or e-mail. The earlier
 * version of this section took raw platform user ids in a textarea, on the
 * grounds that the server accepted any well-formed uuid without checking it
 * belonged to the tenant — so a picker would have implied a guarantee that did
 * not exist. That reasoning was sound and its conclusion has expired: PB-4734
 * made `grantAccess` refuse a user who is not an active member, using the same
 * membership predicate as the connection and execution paths, so the picker now
 * offers exactly what the server will accept. Expecting an administrator to know
 * a colleague's UUID was never the point of that field — it stood in for a
 * promise the backend had not made yet.
 *
 * `PeopleField` over the tenant directory search is the SAME field and the
 * same search behind owner assignment and the KPI owner field, in grant mode
 * `none`: an integration is not a workspace access list. The search is
 * tenant-scoped to active members in auth's own SQL; nothing here re-derives
 * it.
 */
export function AccessGrants({
  integrationId,
  api,
}: {
  integrationId: string;
  api: Api;
}): ReactElement {
  const queryClient = useQueryClient();
  // Staged, not granted on selection. The API is deliberately a bulk one, and
  // granting per click would turn a five-person decision into five separate
  // administrative acts in the audit log.
  const [staged, setStaged] = useState<{ id: string; name: string }[]>([]);

  const grants = useQuery({
    queryKey: ["integration-grants", integrationId],
    queryFn: () => api.listGrants(integrationId),
  });

  const grantedIds = useMemo(
    () => (grants.data ?? []).map((g) => g.userId),
    [grants.data],
  );

  // Names for the rows that already exist. Unresolvable ids are simply absent
  // from the result — someone who left the tenant has no name to disclose — so
  // such a row falls back to the raw id, which is now a useful signal rather
  // than an eyesore: it marks a grant the server would no longer accept.
  const names = useQuery({
    queryKey: ["integration-grant-names", integrationId, grantedIds],
    queryFn: ({ signal }) => resolveDirectoryNames(grantedIds, signal),
    enabled: grantedIds.length > 0,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ["integration-grants", integrationId],
    });
    setStaged([]);
  };

  // Anyone already granted, or already staged, is dropped from the results.
  // Re-granting is a harmless no-op server-side, but offering a name that
  // cannot change anything wastes the administrator's click.
  const alreadyOffered = useMemo(
    () => new Set([...grantedIds, ...staged.map((p) => p.id)]),
    [grantedIds, staged],
  );
  // No "Assign to me" row: granting is an administrative act about others.
  const source = useMemo(
    () =>
      createTenantPeopleSource({
        search: async (query, cursor, signal) => {
          const page = await searchTenantPeople(query, cursor, signal);
          return {
            ...page,
            items: page.items.filter((p) => !alreadyOffered.has(p.id)),
          };
        },
      }),
    [alreadyOffered],
  );

  const grant = useMutation({
    mutationFn: () =>
      api.grantAccess(
        integrationId,
        staged.map((p) => p.id),
      ),
    onSuccess: refresh,
  });
  /* Who the open confirmation is about, or null for closed. Holds the PERSON
     rather than a boolean so the dialog can name them — "Remove Amara Osei?"
     is answerable; "Remove this person?" asks the administrator to remember
     which row they clicked.

     Declared BEFORE the mutation below, which reads `setRevokeTarget` in its
     success path. The closure only runs after render, so the previous order was
     not a live TDZ error — but it relied on that timing, and a `const` used
     above its declaration is the kind of thing that becomes one after an
     innocent refactor. */
  const [revokeTarget, setRevokeTarget] = useState<{
    readonly userId: string;
    /** Their name, or null when the directory could not resolve one. */
    readonly label: string | null;
  } | null>(null);

  const revoke = useMutation({
    mutationFn: (userId: string) => api.revokeAccess(integrationId, [userId]),
    /* RETURNS the refresh so the mutation stays pending until the grants list
       has actually been invalidated. Fire-and-forget closed the dialog first
       and left the stale row on screen for a beat, which reads as a revoke
       that did not take. */
    onSuccess: async (_result, userId) => {
      /* `finally`, because a rejected refresh must not strand the dialog. The
         revoke itself has already SUCCEEDED by this point — if invalidation
         then throws, leaving the dialog open would report a failure that did
         not happen, with `isError` false so no message explains it. Closing
         either way is the honest outcome; a stale list corrects on the next
         read. */
      try {
        await refresh();
      } finally {
        /* Closes ITS OWN dialog, not whichever one happens to be open. The
           dialog is dismissible while a revoke is in flight (`Modal` defaults
           to `dismissOnEscape`) and `revoke.reset()` does not cancel the
           request, so: revoke A, press Escape, open B — and an unconditional
           clear would shut B's dialog when A settled, under an administrator
           who is reading it.

           Compared by userId rather than trusting there to be only one open.
           Functional form so the comparison is against the CURRENT target
           rather than whatever this callback closed over. */
        setRevokeTarget((current) =>
          current?.userId === userId ? null : current,
        );
      }
    },
  });

  return (
    <div className="space-y-3 border-t border-pb-border pt-5">
      <div className="space-y-1">
        <h4 className="text-sm font-semibold">Who can use this</h4>
        <p className="text-xs text-pb-text-muted">
          Only the people listed here can connect their own account and send
          work. Enabling the integration is not enough on its own.
        </p>
      </div>

      {grants.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : (grants.data ?? []).length === 0 ? (
        <p className="text-xs text-pb-text-muted">
          Nobody yet — this integration is enabled but nobody can use it.
        </p>
      ) : (
        <ul className="space-y-1">
          {(grants.data ?? []).map((g) => (
            <li
              key={g.userId}
              className="flex items-center justify-between gap-3 text-xs"
            >
              {/* The id stays the key and the revoke argument — only the LABEL
                  is resolved. Acting on the name would make the display the
                  contract, and two colleagues share a name often enough. */}
              {names.data?.[g.userId] === undefined ? (
                <span
                  className="font-mono"
                  title="This person is no longer in the workspace directory"
                >
                  {g.userId}
                </span>
              ) : (
                <span>{names.data[g.userId]}</span>
              )}
              {/* Opens the confirmation rather than revoking, matching the
                  invite-revoke and mail-disable precedents elsewhere in the
                  console: taking someone's access away is not undoable from
                  here, and a misclick on a dense list is easy.

                  `disabled` is scoped to the row being revoked via
                  `revoke.variables`. It used to be the bare `revoke.isPending`,
                  which greys out EVERY row's button while any one revoke is in
                  flight — the rows are unrelated, and the list looks frozen. */}
              <Button
                variant="ghost"
                onClick={() =>
                  setRevokeTarget({
                    userId: g.userId,
                    /* `null` when the directory cannot resolve them, NOT the
                       raw id. The row shows the id with an explanatory tooltip
                       because there is nothing better to identify it by, but a
                       dialog heading reading "Remove 3f2a8c…?" asks somebody to
                       confirm a destructive act against a string they cannot
                       recognise. The dialog says so in words instead. */
                    label: names.data?.[g.userId] ?? null,
                  })
                }
                disabled={revoke.isPending && revoke.variables === g.userId}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <FormField label="Add people">
          {/* Always empty: a pick is staged below at once, so the field is
              ready for the next person rather than holding the last one as
              though it were still pending. */}
          <PeopleField
            label="Add people"
            emptyLabel="Choose a person"
            value={null}
            source={source}
            labels={{
              noResultsHint:
                "People who already have access, or are added below, are not listed.",
            }}
            onChange={(person) => {
              if (person?.kind !== "person") return;
              setStaged((current) =>
                current.some((p) => p.id === person.id)
                  ? current
                  : [...current, { id: person.id, name: person.name }],
              );
            }}
          />
        </FormField>

        {staged.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {staged.map((person) => (
              <li
                key={person.id}
                className="flex items-center gap-1 rounded-full border border-pb-border px-2 py-1 text-xs"
              >
                <span>{person.name}</span>
                <Button
                  variant="ghost"
                  aria-label={`Remove ${person.name}`}
                  onClick={() =>
                    setStaged((current) =>
                      current.filter((p) => p.id !== person.id),
                    )
                  }
                >
                  ×
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        <Button
          variant="primary"
          onClick={() => grant.mutate()}
          disabled={grant.isPending || staged.length === 0}
        >
          {staged.length > 1 ? `Grant ${staged.length} people` : "Grant"}
        </Button>
      </div>

      {grant.isError ? (
        // Names the tenant-membership case, because it is the one failure an
        // administrator can act on: everything else here is a transient or a
        // bug, but this one means the person genuinely is not in the workspace
        // and has to be invited first.
        <p role="alert" className="text-xs text-pb-red-text">
          Could not grant access. Everyone selected must be an active member of
          this workspace.
        </p>
      ) : null}
      {grant.data !== undefined && grant.data.unchanged.length > 0 ? (
        <p className="text-xs text-pb-text-muted">
          {grant.data.unchanged.length} already had access.
        </p>
      ) : null}

      {/* Confirmation, following the console's existing destructive-action
          precedents (invite revoke, mail-server disable): a question for a
          title, the consequence spelled out, and Cancel before the red
          button. */}
      <ConfirmDialog
        open={revokeTarget !== null}
        onClose={() => {
          setRevokeTarget(null);
          // Clear a previous failure so reopening does not show a stale error
          // about an attempt the administrator has already moved past.
          revoke.reset();
        }}
        title={
          revokeTarget?.label == null
            ? "Remove this person's access?"
            : `Remove ${revokeTarget.label}?`
        }
        confirmLabel={revoke.isPending ? "Removing…" : "Remove access"}
        confirmDisabled={revoke.isPending}
        onConfirm={() => {
          if (revokeTarget !== null) revoke.mutate(revokeTarget.userId);
        }}
      >
        <div className="flex flex-col gap-4">
          {/* Named when the directory knows them; described in words when it
              does not. The row still shows the raw id — there is nothing else
              to identify that entry by — but a heading asking somebody to
              confirm a destructive act against "3f2a8c…" is not a question
              anyone can answer. */}
          {revokeTarget?.label == null ? (
            <p className="text-pb-text">
              This person is no longer in the workspace directory, so their name
              cannot be shown. Their access is listed against the id in the row
              you clicked.
            </p>
          ) : null}
          <p className="text-pb-text">
            They will no longer be able to use this integration, and anything
            they run through it will stop working. Their existing connection is
            kept, so granting access again restores it without them
            reconnecting.
          </p>
          {/* Inside the dialog on purpose: a failed revoke leaves this open,
              and the same message rendered in the section behind would sit
              under the overlay where nobody can read it. */}
          {revoke.isError ? (
            <p role="alert" className="text-sm text-pb-red-text">
              Could not remove their access. They still have it — try again.
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </div>
  );
}
